import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { useTimeoutEffect } from "@react-hookz/web";
import { useCallback, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useCachedAssets } from "../../../../assets/hooks/use-cached-assets";
import { useAccountLimitsContext } from "../../../../context/account-limit-context";
import { useMomentFormSurveys } from "../../../../survey/hooks/use-moment-form-surveys";
import { useLexicalContext } from "../../lexical-context";
import { ListenStatusWrapper } from "../button/listen-status-wrapper";
import { useSurveysEnabledLexical } from "../hooks/use-surveys-enabled";
import { LinkTargetUrlField } from "../link-target-url-field";
import { getSurveyIdFromUrl } from "../utils/survey-url";
import { LinkFormActions } from "./link-form-actions";
import { LinkTextField } from "./link-text-field";
import { defaultSchema, rangeAddSchema } from "./types";

import type { TagGroup } from "../../../../merge-tag/components/types";
import type { EditorLinkData } from "../../plugins/toolbar/hooks/use-link-button-on-click";
import type { LinkType } from "../button/types";
import type { Link } from "./types";
import type { FC } from "react";
import type { Resolver } from "react-hook-form";

export const LinkForm: FC<{
  onRemove: () => void;
  onCancel: () => void;
  onConfirm: (link: Link) => void;
  disabled?: boolean;
  filterTags: (x: string) => TagGroup;
  data: EditorLinkData;
}> = ({ onRemove, onCancel, onConfirm, disabled, filterTags, data }) => {
  const { mode, type } = data;

  const inputRef = useRef<HTMLInputElement>(null);

  useTimeoutEffect(() => inputRef.current?.focus(), 100);

  const { listen_entitlement } = useAccountLimitsContext();
  const { format } = useLexicalContext();
  const surveyjs = useSurveysEnabledLexical();
  const assetsLoader = useCachedAssets();
  const surveyLoader = useMomentFormSurveys({ allowedStatuses: ["active"] });

  const form = makeForm(data, format);

  const methods = useForm(form);
  const { handleSubmit, reset, setValue, getValues, control } = methods;

  const url = useWatch({ control, name: "url" });
  const [manualLinkType, setManualLinkType] = useState<LinkType["type"] | null>(null);

  const linkOption = useMemo<LinkType>(() => {
    const survey_id = getSurveyIdFromUrl(url);

    if (type === "survey_link") {
      return { type: "link_survey", id: survey_id ?? undefined, channel: false };
    }

    if (manualLinkType) {
      if (manualLinkType === "link_survey") {
        return {
          type: "link_survey",
          id: survey_id ?? undefined,
          channel: surveyjs.enabled && surveyjs.type === "channel",
        };
      }
      return { type: manualLinkType };
    }

    if (surveyjs.enabled && survey_id) {
      return {
        type: "link_survey",
        id: survey_id,
        channel: surveyjs.type === "channel",
      };
    }

    return { type: "link_url" };
  }, [manualLinkType, surveyjs, type, url]);

  const showLinkTargetOptions = type !== "survey_link";
  const showEmailCommunicationOption = format === "email" || format === "sms";

  const setBlankLinkText = useCallback(
    (title: string) => {
      const currentText = getValues("text");
      if (mode === "add" && ["range", "asset"].includes(type) && !currentText?.trim()) {
        setValue("text", title, { shouldDirty: true, shouldValidate: true });
      }
    },
    [getValues, mode, setValue, type],
  );

  const confirm = useCallback(
    (link: Link) => {
      onConfirm({ ...link, type: linkOption.type });
      reset();
    },
    [linkOption.type, onConfirm, reset],
  );

  const disableSubmitButton =
    linkOption.type === "link_survey" &&
    (surveyLoader.data?.length === 0 || surveyLoader.loading) &&
    listen_entitlement !== "Disabled";

  return (
    <FormProvider {...methods}>
      <Dialog
        onClose={onCancel}
        open={true}
        slotProps={{
          paper: {
            sx: {
              minWidth: "400px",
            },
          },
        }}
      >
        <DialogTitle>
          {mode === "add" ? (type === "asset" ? "Insert as link" : "Add link") : "Link"}
        </DialogTitle>
        <form onSubmit={handleSubmit(confirm)}>
          <FormControl variant="standard" sx={{ my: 1, minWidth: 300 }} fullWidth>
            <DialogContent sx={{ pt: 0 }}>
              <Stack direction="column" spacing={2}>
                {showLinkTargetOptions && (
                  <RadioGroup
                    row
                    id="link-option-radio"
                    value={linkOption.type}
                    aria-label="Link target"
                    onChange={(e) => {
                      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                      const newType = e.target.value as LinkType["type"];
                      setValue("url", "");
                      setManualLinkType(newType);
                    }}
                    sx={{
                      width: "100%",
                      display: "flex",
                    }}
                  >
                    <FormControlLabel
                      value="link_url"
                      control={<Radio id="lexical-link-dialog-link-url" />}
                      label="URL"
                    />
                    {surveyjs.enabled && (
                      <FormControlLabel
                        value="link_survey"
                        control={<Radio id="lexical-link-dialog-link-survey" />}
                        label="Survey"
                      />
                    )}
                    <FormControlLabel
                      value="link_lib"
                      control={<Radio id="lexical-link-dialog-link-lib" />}
                      label="Content Library"
                    />
                    {showEmailCommunicationOption && (
                      <FormControlLabel
                        value="link_email"
                        control={<Radio id="lexical-link-dialog-link-email" />}
                        label="Email Communication"
                      />
                    )}
                  </RadioGroup>
                )}
                {format !== "sms" && (
                  <LinkTextField
                    filterTags={filterTags}
                    disabled={disabled}
                    data={{ mode, type }}
                  />
                )}
                <ListenStatusWrapper
                  listenEntitlement={listen_entitlement}
                  showListenIntent={linkOption.type === "link_survey"}
                  surveyLoader={surveyLoader}
                  handleClickLearnMore={onCancel}
                >
                  <Controller
                    name="url"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <LinkTargetUrlField
                        linkOption={linkOption}
                        value={field.value}
                        onChange={field.onChange}
                        onSelectedTitle={setBlankLinkText}
                        error={error}
                        assetsLoader={assetsLoader}
                        surveyLoader={surveyLoader}
                        filterTags={filterTags}
                        urlLabel="Link URL"
                        urlPlaceholder="https://www.example.com"
                        useRawAssetPublicUrl={true}
                        disabled={disabled}
                      />
                    )}
                  />
                </ListenStatusWrapper>
              </Stack>
            </DialogContent>
            <LinkFormActions
              onRemove={onRemove}
              onCancel={onCancel}
              data={{ mode, url }}
              disabled={disableSubmitButton}
            />
          </FormControl>
        </form>
      </Dialog>
    </FormProvider>
  );
};

function makeForm(
  { mode, type, url }: EditorLinkData,
  format: "email" | "chat" | "sms",
): {
  defaultValues: Link;
  resolver: Resolver<Link>;
} {
  if (type === "survey_link") {
    return {
      defaultValues: { text: "survey_link", url: url || "" },
      resolver: yupResolver(defaultSchema) as Resolver<Link>,
    };
  }

  // SMS has no Link Text field, so text must not be required.
  if (format !== "sms" && mode === "add" && ["range", "asset"].includes(type)) {
    return {
      defaultValues: { text: "", url: type === "asset" ? url : "" },
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      resolver: yupResolver(rangeAddSchema) as Resolver<Link>,
    };
  }

  return {
    defaultValues: { text: "", url: url ?? "" },
    resolver: yupResolver(defaultSchema),
  };
}
